// src/context/AuthContext.js

import React, { createContext, useContext, useCallback, useEffect, useState } from "react";
import { auth } from "../lib/firebase";
import { onAuthStateChanged, signOut, getRedirectResult } from "firebase/auth";
import { ensureUserDocument } from "../services/userService";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [userInfo, setUserInfo] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  useEffect(() => {
    // Handle redirect result (for mobile/browsers that block popups)
    getRedirectResult(auth).catch((err) => {
      console.warn('getRedirectResult error:', err.message);
    });

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const idToken = await user.getIdToken();
          const mongoUser = await ensureUserDocument(user, idToken);
          
          // Set user WITH role from MongoDB
          setUserInfo({
            ...user,
            role: mongoUser?.role || 'user'
          });
        } catch (err) {
          console.warn('ensureUserDocument failed:', err.message);
          // Even if it fails, set the user with default role
          setUserInfo({
            ...user,
            role: 'user'
          });
        }
      } else {
        setUserInfo(null);
      }
      setLoadingUser(false);
    });

    return () => unsubscribe();
  }, []);

  const logout = async () => {
    await signOut(auth);
  };

  // Stable reference — only changes when auth state changes
  const getIdToken = useCallback(async () => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return null;
    return firebaseUser.getIdToken();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        userInfo,
        isAuthenticated: !!userInfo,
        loadingUser,
        logout,
        getIdToken,
      }}
    >
      {!loadingUser && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
